import React from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * MentionText — render user prose with @mentions highlighted and tappable.
 * Used by signals, the club forum, and direct messages.
 *
 * A mention is an "@" at a word boundary followed by a handle (letters, digits,
 * underscore). Each becomes a cyan-highlighted, clickable token that opens that
 * person's Portal at /{handle}. Everything else renders as plain text — no
 * dangerouslySetInnerHTML, so user text can never inject markup.
 *
 * Renders inside clickable cards (SignalCard wraps this in a button) and inside
 * message bubbles, so the mention click stops propagation — tapping a name opens
 * the profile, not whatever it sits in.
 */
// The leading group keeps the boundary char (start / space / paren) as plain text
// so we don't linkify emails like foo@bar.com (the @ there isn't at a boundary).
const MENTION_RE = /(^|[\s([{])@([a-zA-Z0-9_]{2,30})/g;

/**
 * The same mentions, for the layer BEHIND a text field.
 *
 * A field can't hold styled spans, so a composer that highlights as you type
 * draws this mirror underneath a transparent-text input and lets the input keep
 * only the caret. That trick survives exactly as long as both layers lay text
 * out identically — so unlike the tappable version this one adds background
 * colour and nothing else. Bold or padding here would shift a glyph by a
 * fraction of a pixel and walk the caret out of alignment across a line.
 *
 * A trailing newline gets a zero-width space after it: a field shows the empty
 * last line, a div collapses it, and without this the mirror scrolls out of step
 * the moment someone ends on Enter.
 */
export function MentionMirror({ text }) {
  if (!text) return null;
  const parts = [];
  let last = 0;
  let m;
  MENTION_RE.lastIndex = 0;
  while ((m = MENTION_RE.exec(text)) !== null) {
    const at = m.index + m[1].length;
    if (at > last) parts.push(text.slice(last, at));
    parts.push(
      <span key={at} style={{ backgroundColor: 'rgba(0,255,255,0.5)', borderRadius: 1 }}>@{m[2]}</span>
    );
    last = MENTION_RE.lastIndex;
  }
  if (last < text.length) parts.push(text.slice(last));
  if (text.endsWith('\n')) parts.push('\u200b');
  return <>{parts}</>;
}

export default function MentionText({ text, className, style }) {
  const navigate = useNavigate();
  if (!text) return null;

  const parts = [];
  let lastIndex = 0;
  let match;
  MENTION_RE.lastIndex = 0;
  while ((match = MENTION_RE.exec(text)) !== null) {
    const lead = match[1];
    const handle = match[2];
    const atIndex = match.index + lead.length; // index of the '@'
    // Everything up to and including the boundary char is plain text.
    if (atIndex > lastIndex) parts.push(text.slice(lastIndex, atIndex));
    const to = `/${handle.toLowerCase()}`;
    parts.push(
      <span
        key={`${atIndex}-${handle}`}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(to); }}
        className="cursor-pointer font-bold text-black hover:underline"
        style={{ backgroundColor: 'rgba(0,255,255,0.4)', padding: '0 2px', borderRadius: 1 }}
        title={`View @${handle}`}
      >
        @{handle}
      </span>
    );
    lastIndex = MENTION_RE.lastIndex;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));

  return <span className={className} style={style}>{parts}</span>;
}
